/**
 * #/competitions — el archivo de torneos de la organización, con su ABM.
 *
 * Es la puerta de entrada a lo que el incumbente (Web Competition Manager) ya
 * le da a la federación: fixture, tabla y ficha de cada competencia. Acá se
 * listan y se **cargan**; lo que se hace DESPUÉS de que la competencia existe
 * —generar el fixture, engancharle los partidos del archivo, mirar la tabla—
 * está en `CompetitionPage`.
 *
 * El alta y la edición muestran el formulario completo (fases, grupos, sistema
 * de puntos y desempates) en vez de mandar a la consola: era el último tramo
 * del ítem 1.2 que seguía siendo `curl`, y es lo que hace falta si en la
 * reunión piden *"cargámelo en vivo"*. Los botones de escritura sólo aparecen
 * con el permiso `competitions:manage` — el API igual lo exige, esto es para no
 * ofrecer un botón que va a rebotar con 403.
 */
import { useState } from 'react';
import type { Competition } from '@scout/core';
import { useT } from '../i18n/use-t';
import type { TFunction } from '../i18n/index';
import { Card, Empty, ErrorBox, Loading, Notice } from '../components/States';
import { errorMessage, useAsync } from '../hooks';
import type { ApiClient, Identity } from '../lib/api-client';
import { COMPETITIONS_PERMISSION, canManageCompetitions } from '../lib/auth';
import { competitionStatusLabelKey } from '../lib/competition';
import { dateTime } from '../lib/format';
import { competitionHash, navigate } from '../lib/router';
import { CompetitionForm } from './competition/CompetitionForm';

export function statusLabel(status: Competition['status'], t: TFunction): string {
  return t(competitionStatusLabelKey(status));
}

/** Qué está abierto en el formulario: nada, un alta, o la edición de una ficha. */
type Editing = { kind: 'new' } | { kind: 'edit'; competition: Competition } | null;

export function CompetitionsPage({
  client,
  identity,
}: {
  client: ApiClient;
  identity: Identity | null;
}): JSX.Element {
  const competitions = useAsync(() => client.listCompetitions(), [client]);
  const t = useT();
  const rows = competitions.data ?? [];

  const puedeGestionar = canManageCompetitions(identity);
  const [editing, setEditing] = useState<Editing>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abrirAlta = (): void => {
    setEditing({ kind: 'new' });
    setNotice(null);
    setError(null);
  };

  const abrirEdicion = (competition: Competition): void => {
    setEditing({ kind: 'edit', competition });
    setNotice(null);
    setError(null);
  };

  const borrar = async (competition: Competition): Promise<void> => {
    // Confirmación explícita: se lleva el fixture y no se deshace.
    if (!window.confirm(t('competitionForm.confirmDelete', { name: competition.name }))) return;
    setError(null);
    try {
      await client.deleteCompetition(competition.id);
      setNotice(t('competitionForm.deletedNotice', { name: competition.name }));
      if (editing?.kind === 'edit' && editing.competition.id === competition.id) setEditing(null);
      competitions.reload();
    } catch (err) {
      setError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1>{t('competition.title')}</h1>
        <div className="row">
          <button type="button" onClick={competitions.reload}>
            {t('common.refresh')}
          </button>
          {puedeGestionar ? (
            <button type="button" className="primary" onClick={abrirAlta}>
              {t('competitionForm.newCompetition')}
            </button>
          ) : null}
        </div>
      </div>
      <p className="hint">{t('competition.subtitle')}</p>

      {notice !== null ? <p className="hint">{notice}</p> : null}
      {error !== null ? <ErrorBox message={error} /> : null}
      {competitions.status === 'error' ? (
        <ErrorBox message={errorMessage(competitions.error)} onRetry={competitions.reload} />
      ) : null}

      {/* Sin el permiso la pantalla se ve igual, pero se explica por qué no hay
          botones en vez de dejar a alguien buscándolos. */}
      {!puedeGestionar && identity !== null ? (
        <Notice
          message={t('competitionForm.noPermission', { permission: COMPETITIONS_PERMISSION })}
        />
      ) : null}

      {editing !== null ? (
        <CompetitionForm
          // La `key` reinicia el estado del formulario al pasar de una ficha a
          // otra (o de editar a crear) sin tener que sincronizar props.
          key={editing.kind === 'new' ? 'nueva' : editing.competition.id}
          client={client}
          initial={editing.kind === 'edit' ? editing.competition : null}
          onSaved={(saved) => {
            setNotice(t('competitionForm.savedNotice', { name: saved.name }));
            const alta = editing.kind === 'new';
            setEditing(null);
            competitions.reload();
            // Recién creada, se entra a la ficha: es donde está el botón de
            // generar el fixture, o sea el paso siguiente de quien acaba de
            // cargarla. Una edición, en cambio, vuelve al listado con el aviso.
            if (alta) navigate(competitionHash(saved.id));
          }}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      <Card>
        {competitions.status === 'loading' && competitions.first ? (
          <Loading what={t('competition.loadingWhat')} />
        ) : null}
        {competitions.status === 'ok' && rows.length === 0 ? (
          <Empty>{t('competition.empty')}</Empty>
        ) : null}
        {rows.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('competition.colName')}</th>
                  <th>{t('competition.colSeason')}</th>
                  <th>{t('competition.colStatus')}</th>
                  <th className="num">{t('competition.colTeams')}</th>
                  <th className="num">{t('competition.colPhases')}</th>
                  <th>{t('competition.colUpdated')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((competition) => (
                  <tr key={competition.id}>
                    <td>
                      <strong>{competition.name}</strong>
                      <div className="hint">{competition.id}</div>
                    </td>
                    <td>{competition.season || '·'}</td>
                    <td>{statusLabel(competition.status, t)}</td>
                    <td className="num">{competition.teams.length}</td>
                    <td className="num">{competition.phases.length}</td>
                    <td>{dateTime(competition.updatedAt)}</td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <a
                          href={competitionHash(competition.id)}
                          style={{ textDecoration: 'none' }}
                        >
                          <button type="button" className="small">
                            {t('competition.open')}
                          </button>
                        </a>
                        {puedeGestionar ? (
                          <>
                            <button
                              type="button"
                              className="small"
                              onClick={() => abrirEdicion(competition)}
                            >
                              {t('competitionForm.edit')}
                            </button>
                            <button
                              type="button"
                              className="small danger"
                              onClick={() => void borrar(competition)}
                            >
                              {t('competitionForm.remove')}
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
